import {react, useState, useEffect} from 'react';
import { useNavigate } from 'react-router-dom';
import getTaskFlows from '../functions/API/getTaskFlows.js'
import postArchiveTaskFlow from '../functions/API/postArchiveTaskFlow.js'
import postUnarchiveTaskFlow from '../functions/API/postUnarchiveTaskFlow.js'
import postDuplicateTaskFlow from '../functions/API/postDuplicateTaskFlow.js'
import AdminSidebar from '../components/admin/AdminSidebar.js';
import { toast } from '../lib/toast.js';
import NewTaskFlowForm from '../forms/newTaskFlow.js';
import SlideOverRight from '../components/slideOverRight.js';
import Loader from '../components/loader.js'
import DeleteModal from '../modals/delete.js'
import ConfirmationModal from '../modals/confirmation.js'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import { ChevronDown, MoreVertical, Pencil, Archive, ArchiveRestore, Trash2, ListOrdered, Copy } from 'lucide-react'
import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'

export default function TaskFlows() {
    const navigate = useNavigate();
    const [newTaskFlowForm, setNewTaskFlowForm] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedTaskFlow, setSelectedTaskFlow] = useState(null);
    const [taskflows, setTaskFlows] = useState();
    const [shouldRefresh, setShouldRefresh] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [taskFlowToDelete, setTaskFlowToDelete] = useState(null);
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [confirmationAction, setConfirmationAction] = useState(null);
    const [actionLoading, setActionLoading] = useState({});
    const [filter, setFilter] = useState('active'); // 'all', 'active', 'archived'
    const [allTaskFlows, setAllTaskFlows] = useState([]);
    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
    const [duplicateName, setDuplicateName] = useState('');
    const [taskFlowToDuplicate, setTaskFlowToDuplicate] = useState(null);
    const [duplicateLoading, setDuplicateLoading] = useState(false);

    async function GetAPIData() {
        let TaskFlowsResponse = await getTaskFlows();

        // Check if response is OK and data exists
        if (TaskFlowsResponse.response.ok && TaskFlowsResponse.data && TaskFlowsResponse.data.task_flows && Array.isArray(TaskFlowsResponse.data.task_flows)) {
            setAllTaskFlows(TaskFlowsResponse.data.task_flows);
        } else {
            // Handle error or empty response
            if (!TaskFlowsResponse.response.ok) {
                toast.error('Failed to load task flows. Please try again.');
            }
            setAllTaskFlows([]);
        }

        setIsLoaded(true);
    }

    function getFilteredTaskFlows() {
        if (filter === 'all') return allTaskFlows;
        if (filter === 'active') return allTaskFlows.filter(tf => !tf.is_archived);
        if (filter === 'archived') return allTaskFlows.filter(tf => tf.is_archived);
        return allTaskFlows;
    }

    function getFilterLabel() {
        if (filter === 'all') return 'All Task Flows';
        if (filter === 'active') return 'Active';
        if (filter === 'archived') return 'Archived';
        return 'All Task Flows';
    }

    function setFormState(){
        setEditMode(false);
        setSelectedTaskFlow(null);
        setNewTaskFlowForm(true);
    }

    function handleEdit(taskFlow){
        setEditMode(true);
        setSelectedTaskFlow(taskFlow);
        setNewTaskFlowForm(true);
    }

    function handleDeleteClick(taskFlow){
        setTaskFlowToDelete(taskFlow);
        setDeleteModalOpen(true);
    }

    async function handleArchive(taskFlow){
        if (actionLoading[taskFlow.id]) return;

        setActionLoading({...actionLoading, [taskFlow.id]: true});

        const response = await postArchiveTaskFlow(taskFlow.id);

        if(response.response.ok){
            toast.success('Task flow archived successfully.');
            setShouldRefresh(shouldRefresh + 1);
        } else {
            toast.error('Failed to archive task flow.');
        }

        setActionLoading({...actionLoading, [taskFlow.id]: false});
    }

    async function handleUnarchive(taskFlow){
        if (actionLoading[taskFlow.id]) return;

        setActionLoading({...actionLoading, [taskFlow.id]: true});

        const response = await postUnarchiveTaskFlow(taskFlow.id);

        if(response.response.ok){
            toast.success('Task flow unarchived successfully.');
            setShouldRefresh(shouldRefresh + 1);
        } else {
            toast.error('Failed to unarchive task flow.');
        }

        setActionLoading({...actionLoading, [taskFlow.id]: false});
    }

    function handleDuplicateClick(taskFlow){
        setTaskFlowToDuplicate(taskFlow);
        setDuplicateName('Copy of ' + taskFlow.name);
        setDuplicateModalOpen(true);
    }

    async function handleDuplicateConfirm(){
        if (!duplicateName.trim() || duplicateLoading) return;

        setDuplicateLoading(true);

        const response = await postDuplicateTaskFlow(taskFlowToDuplicate.id, duplicateName.trim());

        if(response.response.ok){
            toast.success('Task flow duplicated successfully.');
            setShouldRefresh(shouldRefresh + 1);
        } else {
            toast.error('Failed to duplicate task flow.');
        }

        setDuplicateLoading(false);
        setDuplicateModalOpen(false);
        setTaskFlowToDuplicate(null);
        setDuplicateName('');
    }

    function refreshData(){
        setShouldRefresh(shouldRefresh + 1);
    }

    useEffect(() => {
        GetAPIData();
    }, [shouldRefresh]);

    const filteredTaskFlows = getFilteredTaskFlows();

    return (
        <>
            <AdminSidebar>
                {loading ? (
                    <Loader style='table' setLoadingState={setLoading} isLoaded={isLoaded}/>
                ) : (
                    <div className="">
                        <div className="sm:flex sm:items-center">
                            <div className="sm:flex-auto">
                                <h1 className="text-2xl text-left leading-6 text-gray-900 dark:text-white col-span-3">Task Flows</h1>
                                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                                    Manage task flows for onboarding, requests, and administrative processes
                                </p>
                            </div>
                            <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none flex items-center gap-2">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="outline">
                                            Filter: {getFilterLabel()}
                                            <ChevronDown className="ml-2 h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                        <DropdownMenuItem onSelect={() => setFilter('all')}>
                                            All Task Flows
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onSelect={() => setFilter('active')}>
                                            Active
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onSelect={() => setFilter('archived')}>
                                            Archived
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                <Button onClick={setFormState}>
                                    Add task flow
                                </Button>
                            </div>
                        </div>

                        <div className="mt-4 flow-root">
                            <div className="overflow-x-auto">
                                <div className="inline-block min-w-full py-2 align-middle lg:pr-2">
                                    <table className="min-w-full divide-y divide-gray-300 dark:divide-zinc-700">
                                        <thead>
                                            <tr>
                                                <th scope="col" className="py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white sm:pl-0">
                                                    Name
                                                </th>
                                                <th scope="col" className="py-3.5 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                                    Description
                                                </th>
                                                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                                                    <span className="sr-only">Actions</span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 dark:divide-zinc-700 bg-white dark:bg-transparent">
                                            {filteredTaskFlows.map((taskFlow) => {
                                                const isArchived = taskFlow.is_archived || false;
                                                const truncatedDescription = taskFlow.description && taskFlow.description.length > 100
                                                    ? taskFlow.description.substring(0, 100) + '...'
                                                    : taskFlow.description;

                                                return (
                                                    <tr key={taskFlow.id} onClick={() => navigate(`/taskflows/${taskFlow.id}`)} className="cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors">
                                                        <td className="whitespace-nowrap py-3 text-sm">
                                                            <span className="font-medium text-gray-900 dark:text-white">
                                                                {taskFlow.name}
                                                            </span>
                                                        </td>
                                                        <td className="py-3 text-sm text-gray-500">
                                                            {truncatedDescription}
                                                        </td>
                                                        <td className="relative whitespace-nowrap py-3 pl-3 pr-4 text-right text-sm font-medium sm:pr-0" onClick={(e) => e.stopPropagation()}>
                                                            <div className="flex items-center justify-end gap-2">
                                                                {isArchived && (
                                                                    <Badge variant="destructive">Archived</Badge>
                                                                )}
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger asChild>
                                                                        <Button variant="ghost" size="icon">
                                                                            <MoreVertical className="h-4 w-4" />
                                                                            <span className="sr-only">Open menu</span>
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    <DropdownMenuItem onSelect={() => navigate(`/taskflows/${taskFlow.id}`)}>
                                                                        <ListOrdered className="mr-2 h-4 w-4" />
                                                                        Manage Steps
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuSeparator />
                                                                    <DropdownMenuItem onSelect={() => handleEdit(taskFlow)}>
                                                                        <Pencil className="mr-2 h-4 w-4" />
                                                                        Edit Flow
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem onSelect={() => handleDuplicateClick(taskFlow)}>
                                                                        <Copy className="mr-2 h-4 w-4" />
                                                                        Duplicate
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem
                                                                        onSelect={() => isArchived ? handleUnarchive(taskFlow) : handleArchive(taskFlow)}
                                                                        disabled={actionLoading[taskFlow.id]}
                                                                    >
                                                                        {isArchived ? (
                                                                            <>
                                                                                <ArchiveRestore className="mr-2 h-4 w-4" />
                                                                                Unarchive
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Archive className="mr-2 h-4 w-4" />
                                                                                Archive
                                                                            </>
                                                                        )}
                                                                    </DropdownMenuItem>
                                                                    {isArchived && (
                                                                        <>
                                                                            <DropdownMenuSeparator />
                                                                            <DropdownMenuItem
                                                                                onSelect={() => handleDeleteClick(taskFlow)}
                                                                                className="text-red-600 focus:text-red-600"
                                                                            >
                                                                                <Trash2 className="mr-2 h-4 w-4" />
                                                                                Delete
                                                                            </DropdownMenuItem>
                                                                        </>
                                                                    )}
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    {filteredTaskFlows.length === 0 && (
                                        <div className="text-center py-8">
                                            <p className="text-sm text-gray-500">No task flows found.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <SlideOverRight visible={newTaskFlowForm} setVisible={setNewTaskFlowForm}>
                    <NewTaskFlowForm
                        setVisible={setNewTaskFlowForm}
                        refresh={setShouldRefresh}
                        refreshValue={shouldRefresh}
                        editMode={editMode}
                        existingData={selectedTaskFlow}
                    />
                </SlideOverRight>
                <DeleteModal
                    open={deleteModalOpen}
                    setOpen={setDeleteModalOpen}
                    IDToDelete={taskFlowToDelete?.id}
                    refresh={refreshData}
                    deleteAction="deleteTaskFlow"
                    title="Delete Task Flow"
                    message={`Are you sure you want to delete "${taskFlowToDelete?.name}"? This action cannot be undone.`}
                />
                <Transition.Root show={duplicateModalOpen} as={Fragment}>
                    <Dialog as="div" className="relative z-10" onClose={setDuplicateModalOpen}>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-100"
                            enterFrom="opacity-0"
                            enterTo="opacity-90"
                            leave="ease-in duration-100"
                            leaveFrom="opacity-90"
                            leaveTo="opacity-0"
                        >
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        </Transition.Child>
                        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                            <div className="flex min-h-full items-start justify-center p-4 text-center sm:items-center sm:p-0">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-out duration-100"
                                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                                    leave="ease-in duration-100"
                                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                >
                                    <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-zinc-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                        <div>
                                            <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                                                Duplicate Task Flow
                                            </Dialog.Title>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    Enter a name for the new task flow. All steps, fields, and configuration will be copied.
                                                </p>
                                            </div>
                                            <div className="mt-4">
                                                <label htmlFor="duplicate-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Name
                                                </label>
                                                <input
                                                    type="text"
                                                    id="duplicate-name"
                                                    className="mt-1 block w-full rounded-md border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                                    value={duplicateName}
                                                    onChange={(e) => setDuplicateName(e.target.value)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') handleDuplicateConfirm(); }}
                                                />
                                            </div>
                                        </div>
                                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                            <button
                                                type="button"
                                                className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto disabled:opacity-50"
                                                onClick={handleDuplicateConfirm}
                                                disabled={!duplicateName.trim() || duplicateLoading}
                                            >
                                                {duplicateLoading ? 'Duplicating...' : 'Duplicate'}
                                            </button>
                                            <button
                                                type="button"
                                                className="inline-flex w-full justify-center rounded-md bg-white dark:bg-zinc-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 hover:bg-gray-50 dark:hover:bg-zinc-600 sm:mt-0 sm:w-auto"
                                                onClick={() => setDuplicateModalOpen(false)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </Dialog>
                </Transition.Root>
            </AdminSidebar>
        </>
    );
}
